import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Radio, Users, Clock, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fetchCommunityHall, subscribeCommunityHall } from "@/lib/chanting/chanting-service";

function formatCountdown(ms) {
  if (ms <= 0) return "Live now";
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/**
 * Phase X — Community Hall (PUBG-style live overview of global chanting rooms).
 */
export function CommunityHall({ compact = false }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchCommunityHall();
      setSnapshot(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const unsub = subscribeCommunityHall((data) => {
      setSnapshot(data);
      setLoading(false);
    });
    return unsub;
  }, [refresh]);

  if (loading && !snapshot) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const rooms = snapshot?.rooms ?? [];

  return (
    <section data-testid="community-hall" className={compact ? "" : "min-h-screen py-24 px-4 noise"}>
      <div className={compact ? "" : "max-w-4xl mx-auto"}>
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-6 h-6 text-amber-500" />
          <h1 className="font-heading text-2xl md:text-3xl">Community Hall</h1>
          <Badge variant="secondary" className="animate-pulse">
            <Radio className="w-3 h-3 mr-1" /> LIVE
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Real-time global chanting rooms — join any wave mid-cycle, perfectly synced with sādhakas worldwide.
        </p>
        {error && <p className="text-destructive text-sm mb-4">{error}</p>}

        <div className="grid gap-4 md:grid-cols-2">
          {rooms.map((room) => {
            const inGap = room.cycle?.in_gap;
            const participants = room.active_participants ?? 0;
            const globalCycles = room.global_stats?.total_cycles_completed_today ?? 0;
            return (
              <Card key={room.chant_id} className="bg-white/90 shadow-card overflow-hidden" data-testid={`hall-room-${room.chant_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start gap-2">
                    <CardTitle className="text-lg font-heading">{room.title}</CardTitle>
                    <Badge variant={inGap ? "outline" : "default"}>{inGap ? "Between waves" : "In progress"}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground capitalize">
                    {room.schedule_mode?.replace("_", " ")} · {Math.round(room.duration_seconds / 60)} min
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {room.cycle_label}
                      </span>
                      {!inGap && <span>{room.progress_percent}%</span>}
                    </div>
                    <Progress value={inGap ? 0 : room.progress_percent} className="h-2" />
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {participants} chanting now
                    </span>
                    <span>{globalCycles.toLocaleString()} cycles today</span>
                    {!inGap ? (
                      <span>Next wave: {formatCountdown(room.ms_until_cycle_end)}</span>
                    ) : (
                      <span>Starts in: {formatCountdown(room.ms_until_next_cycle)}</span>
                    )}
                  </div>
                  <Button size="sm" className="w-full" asChild>
                    <Link to={`/global-chanting/${room.chant_id}`}>Join Now</Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {!compact && (
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/global-chanting">All chants</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/townhall">VedaPath Townhall</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/prayer-hall">← Dhyāna Mandir</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

export function CommunityHallPage() {
  return <CommunityHall />;
}
